/**
 * CompanySettings - General company configuration
 *
 * Features:
 * - Company name and branding
 * - Default settings
 * - Processing preferences
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchCompanySettings, updateCompanySettings } from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './CompanySettings.module.css';

export default function CompanySettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanySettings();
      setSettings(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateCompanySettings(settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading settings...</p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className={styles.error}>
        Failed to load settings. Please try again.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Settings</h2>
        <p className={styles.subtitle}>
          General company configuration and preferences
        </p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {success && (
        <div className={styles.successBanner}>
          Settings saved successfully
        </div>
      )}

      <div className={styles.sections}>
        {/* Company Info */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Company Information</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Company Name</label>
            <input
              type="text"
              className={styles.input}
              value={settings.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Your Company Name"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Contact Email</label>
            <input
              type="email"
              className={styles.input}
              value={settings.contact_email || ''}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              placeholder="contact@company.com"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Website</label>
            <input
              type="url"
              className={styles.input}
              value={settings.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://company.com"
            />
          </div>
        </section>

        {/* Processing Defaults */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Processing Defaults</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Default Output Quality</label>
            <select
              className={styles.select}
              value={settings.default_quality || 95}
              onChange={(e) => handleChange('default_quality', parseInt(e.target.value))}
            >
              <option value={100}>100% (Maximum)</option>
              <option value={95}>95% (Recommended)</option>
              <option value={90}>90%</option>
              <option value={85}>85%</option>
            </select>
            <p className={styles.hint}>
              JPEG quality for exported photos
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Default Export Size</label>
            <select
              className={styles.select}
              value={settings.default_export_size || 'original'}
              onChange={(e) => handleChange('default_export_size', e.target.value)}
            >
              <option value="original">Original Size</option>
              <option value="4000">4000px (Long Edge)</option>
              <option value="3000">3000px (Long Edge)</option>
              <option value="2000">2000px (Long Edge)</option>
              <option value="1500">1500px (Long Edge)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.auto_watermark || false}
                onChange={(e) => handleChange('auto_watermark', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Add watermark to exports</span>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.preserve_exif || true}
                onChange={(e) => handleChange('preserve_exif', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Preserve EXIF metadata in exports</span>
            </label>
          </div>
        </section>

        {/* Workflow */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Workflow</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Auto-skip Threshold</label>
            <select
              className={styles.select}
              value={settings.auto_skip_threshold || 'none'}
              onChange={(e) => handleChange('auto_skip_threshold', e.target.value)}
            >
              <option value="none">Disabled</option>
              <option value="0.3">Very Low Quality (0.3)</option>
              <option value="0.4">Low Quality (0.4)</option>
              <option value="0.5">Medium Quality (0.5)</option>
            </select>
            <p className={styles.hint}>
              Automatically skip photos below this quality score
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.require_face_assignment || false}
                onChange={(e) => handleChange('require_face_assignment', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Require all faces to be assigned before marking done</span>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.auto_export_on_done || false}
                onChange={(e) => handleChange('auto_export_on_done', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Automatically export photos when marked done</span>
            </label>
          </div>
        </section>

        {/* Cropping Preferences */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Cropping Preferences</h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>Landscape Aspect Ratios</label>
            <div className={styles.ratioOptions}>
              {['3:2', '4:3', '16:9', '1:1'].map((ratio) => {
                const selected = settings.cropping_preferences?.landscape_ratios?.includes(ratio) ?? (ratio === '3:2');
                return (
                  <button
                    key={ratio}
                    type="button"
                    className={`${styles.ratioChip} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const current = settings.cropping_preferences?.landscape_ratios || ['3:2'];
                      const updated = selected
                        ? current.filter(r => r !== ratio)
                        : [...current, ratio];
                      handleChange('cropping_preferences', {
                        ...settings.cropping_preferences,
                        landscape_ratios: updated.length > 0 ? updated : ['3:2'],
                      });
                    }}
                  >
                    {ratio}
                  </button>
                );
              })}
            </div>
            <p className={styles.hint}>
              Available aspect ratios for landscape crops
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Portrait Aspect Ratios</label>
            <div className={styles.ratioOptions}>
              {['2:3', '3:4', '9:16', '1:1'].map((ratio) => {
                const selected = settings.cropping_preferences?.portrait_ratios?.includes(ratio) ?? (ratio === '2:3');
                return (
                  <button
                    key={ratio}
                    type="button"
                    className={`${styles.ratioChip} ${selected ? styles.selected : ''}`}
                    onClick={() => {
                      const current = settings.cropping_preferences?.portrait_ratios || ['2:3'];
                      const updated = selected
                        ? current.filter(r => r !== ratio)
                        : [...current, ratio];
                      handleChange('cropping_preferences', {
                        ...settings.cropping_preferences,
                        portrait_ratios: updated.length > 0 ? updated : ['2:3'],
                      });
                    }}
                  >
                    {ratio}
                  </button>
                );
              })}
            </div>
            <p className={styles.hint}>
              Available aspect ratios for portrait crops
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.cropping_preferences?.auto_level_enabled ?? true}
                onChange={(e) => handleChange('cropping_preferences', {
                  ...settings.cropping_preferences,
                  auto_level_enabled: e.target.checked,
                })}
                className={styles.checkbox}
              />
              <span>Auto-apply detected rotation (leveling)</span>
            </label>
            <p className={styles.hint}>
              Automatically correct tilted photos based on detected horizon
            </p>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Leveling Confidence Threshold</label>
            <select
              className={styles.select}
              value={settings.cropping_preferences?.level_confidence_threshold ?? 0.75}
              onChange={(e) => handleChange('cropping_preferences', {
                ...settings.cropping_preferences,
                level_confidence_threshold: parseFloat(e.target.value),
              })}
              disabled={!settings.cropping_preferences?.auto_level_enabled}
            >
              <option value={0.9}>Very High (90%+)</option>
              <option value={0.8}>High (80%+)</option>
              <option value={0.75}>Medium (75%+) - Recommended</option>
              <option value={0.65}>Low (65%+)</option>
            </select>
            <p className={styles.hint}>
              Only apply auto-leveling when detection confidence exceeds this threshold
            </p>
          </div>
        </section>

        {/* Notifications */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Notifications</h3>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.notify_on_upload || true}
                onChange={(e) => handleChange('notify_on_upload', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Email notification when photos are uploaded</span>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings.notify_on_export || false}
                onChange={(e) => handleChange('notify_on_export', e.target.checked)}
                className={styles.checkbox}
              />
              <span>Email notification when photos are exported</span>
            </label>
          </div>
        </section>
      </div>

      <div className={styles.footer}>
        <Button variant="ghost" onClick={loadSettings} disabled={saving}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
